import React, { memo } from 'react';
import { Blok } from '../../interfaces/Blok.interface';
import { withBlokWrapper } from '../BlokWrapper/BlokWrapper';
import Image from '../Image/Image';
import Grid from '../Utils/Grid/Grid';
import Col from '../Utils/Col/Col';
import Heading, { HeadingTypes } from '../Heading/Heading';
import { StoryblokNode } from '../../interfaces/StoryblokNode.interface';
import Container from '../Utils/Container/Container';
import moment from 'moment';
import Subtitle from '../Subtitle/Subtitle';
import ButtonLink from '../ButtonLink/ButtonLink';
import { ButtonSize } from '../../interfaces/enums/ButtonSize.enum';
import { ButtonType } from '../../interfaces/enums/ButtonType.enum';
import { StoryblokArticle } from '../../interfaces/StoryblokArticle.interface';
import { pushToDataLayer } from '../../utils/pushToDataLayer/pushToDataLayer.util';
import SbImage from '../SbImage/SbImage';

export interface FeaturedArticleCardProps extends Blok {
  article: StoryblokNode<StoryblokArticle>;
  button_label: string;
}

interface FeaturedArticleCardBlokProps {
  blok: FeaturedArticleCardProps;
}

const FeaturedArticleCard: React.FC<FeaturedArticleCardBlokProps> = ({
  blok,
}) => {
  const {
    read_time = undefined,
    published_date_override = undefined,
    preview_card_image,
    preview_card_overline,
    preview_card_heading,
    preview_card_body,
  } = blok?.article?.content;
  const { published_at, full_slug } = blok?.article;
  const { button_label } = blok;

  const handleDataLayerClickEvent = (articleTitle: string): void => {
    pushToDataLayer({
      event: 'article_click',
      data: { article_title: articleTitle },
    });
  };

  return (
    <Container>
      <div className="py-20 xl:pb-0 xl:pt-28">
        <Grid className="grid-flow-row-dense" gapY="gap-16">
          <Col span={{ lg: 6 }} start={{ lg: 7 }}>
            {preview_card_image && (
              <SbImage
                asset={preview_card_image}
                options={{ width: 600, sizes: { base: 1200 } }}
              />
            )}
          </Col>
          <Col span={{ lg: 5 }}>
            <div className="h-full flex items-center">
              <div>
                {preview_card_overline && (
                  <Subtitle text={preview_card_overline} className="pb-3" />
                )}
                {preview_card_heading && (
                  <Heading
                    text={preview_card_heading}
                    headingType={HeadingTypes.h2}
                  />
                )}
                <p className="text-text/body-tint text-sm pt-3">
                  {(published_date_override || published_at) && (
                    <span>
                      {moment(published_date_override || published_at).format(
                        'MMMM Do YYYY'
                      )}
                    </span>
                  )}
                  {(published_date_override || published_at) && read_time && (
                    <span className="px-1">-</span>
                  )}
                  {read_time && <span>{read_time} min read</span>}
                </p>
                {preview_card_body && (
                  <p className="text-text/body-tint text-base font-normal mt-6">
                    {preview_card_body}
                  </p>
                )}
                {full_slug && button_label && (
                  <ButtonLink
                    url={full_slug}
                    label={button_label}
                    size={ButtonSize.XL}
                    type={ButtonType.SECONDARY}
                    className="mt-8  w-full md:w-auto"
                    onClick={() =>
                      handleDataLayerClickEvent(preview_card_heading)
                    }
                  >
                    {button_label}
                  </ButtonLink>
                )}
              </div>
            </div>
          </Col>
        </Grid>
      </div>
    </Container>
  );
};

export default memo(withBlokWrapper(FeaturedArticleCard));
